/**
 * One collapsible worklist group: severity dot + problem label + count in the
 * header with the operator action sentence, and the group's vehicles fetched
 * lazily on first expand. Visual spec: the approved audit-remodel mockup
 * artifact `.group`/`.ghead` (bordered card, caret, right-aligned action in
 * secondary ink).
 *
 * The severity is a colored dot, not the filled CRITICAL/WARNING pill the
 * mockup had (Ian, 2026-09-28: the word repeated on every row was clutter).
 * The list names each severity once in a heading above its groups, and the dot
 * carries the word for screen readers and on hover.
 */
import React, { useEffect, useState } from "react"

import {
  getGroupFilterOptions,
  listProblemVehicles,
  GroupFilterOptionsResponse,
  VehicleResponse,
} from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { ProblemTypeInfo } from "../../../api/audit"
import { useIsMobile } from "../../../hooks/useIsMobile"
import { formatDaysOpen } from "../../../utils/formatAge"
import GroupFilterBar, {
  countActiveFilters,
  EMPTY_GROUP_FILTERS,
  GroupFilters,
} from "./GroupFilterBar"
import GroupVehicleTable, { GroupSort } from "./GroupVehicleTable"
import SeverityDot from "./SeverityDot"

interface Props {
  runId: number
  /** Page-level store view (rooftop slug) — null shows the whole group. */
  rooftopSlug: string | null
  slug: string
  count: number
  info: ProblemTypeInfo | null
  expanded: boolean
  onToggle: () => void
  /** VINs first flagged with this problem in this run — passed for WARNING
   *  groups only; a critical group shows days open instead of NEW. */
  newVins?: Set<string>
  /** Critical groups: when INVI first flagged the group's oldest car. */
  oldestFirstSeenAt?: string | null
  /** vin → marked_at for the operator's worked marks on this problem. */
  workedMarks?: Map<string, string>
  onRowClick?: (vehicle: VehicleResponse) => void
}

/** The one group that refuses sorting — see the table below. */
const DEALER_DUPLICATE_SLUG = "duplicate-vin"

/** Rows fetched per page — the backend caps size at 500. */
const GROUP_PAGE_SIZE = 200

/** The group's one-line states — loading, empty, failed — share this row. */
const messageStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
  padding: THEME.spacing.md,
  fontSize: 12.5,
  color: THEME.colors.neutral.light.textSecondary,
}

// Matches the filter bar's Clear button (GroupFilterBar.tsx:152-167) — the
// other small action offered inside an expanded group.
const retryButtonStyle: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: 12,
  fontFamily: THEME.fonts.primary,
  borderRadius: THEME.borderRadius.sm,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
}

export default function WorklistGroup({
  runId, rooftopSlug, slug, count, info, expanded, onToggle, newVins, oldestFirstSeenAt,
  workedMarks, onRowClick,
}: Props) {
  const [vehicles, setVehicles] = useState<VehicleResponse[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  // null = the group's own default order (oldest inventory, or oldest
  // departure where the rows carry dates).
  const [sort, setSort] = useState<GroupSort | null>(null)
  const [filters, setFilters] = useState<GroupFilters>(EMPTY_GROUP_FILTERS)
  const [filterOptions, setFilterOptions] = useState<GroupFilterOptionsResponse | null>(null)
  // Action text yields the row to title + count on phones (mockup: gaction
  // hidden on narrow screens).
  const isMobile = useIsMobile()
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Bumped by Retry. A failed fetch leaves `loadedKey` behind, so without
  // something in the request key that the operator can change, there is no
  // input that would make the effect run again — the group would stay on its
  // error forever.
  const [reloadNonce, setReloadNonce] = useState(0)

  // Fetch the current page the first time the group opens, and again whenever
  // the pager moves or the sort changes (per run change the component
  // remounts, resetting all of this). Big groups (Completed at 1,650) page
  // rather than truncate at 200, which is also why sorting is server-side —
  // reordering the 200 rows in hand would answer a question about page one.
  const filterKey = JSON.stringify(filters)
  const requestKey =
    `${page}|${sort ? `${sort.field}:${sort.direction}` : ""}|${filterKey}|${rooftopSlug ?? ""}|${reloadNonce}`
  useEffect(() => {
    if (!expanded || requestKey === loadedKey) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await listProblemVehicles(
          runId, slug, page, GROUP_PAGE_SIZE, sort, filters, rooftopSlug,
        )
        if (!cancelled) {
          setVehicles(response.vehicles)
          setTotal(response.total)
          setLoadedKey(requestKey)
          // A load that succeeds clears the last failure. Without this the
          // error branch outranks the rows it just fetched and the group shows
          // a stale error over live data.
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load this group")
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [expanded, page, sort, filters, requestKey, loadedKey, runId, slug, rooftopSlug, reloadNonce])

  // A store-view change redefines what the group holds, so the per-group
  // filters and pager reset with it — a Stock-type choice made against the
  // whole group could name a type the narrowed group doesn't contain.
  // Options are nulled so the effect below refetches them for the new scope.
  const [optionsScope, setOptionsScope] = useState<string | null>(rooftopSlug)
  useEffect(() => {
    if (rooftopSlug === optionsScope) return
    setOptionsScope(rooftopSlug)
    setFilterOptions(null)
    setFilters(EMPTY_GROUP_FILTERS)
    setPage(1)
  }, [rooftopSlug, optionsScope])

  // Options describe the WHOLE group (within the page-level store view), so
  // they are fetched once per group-and-scope and never re-fetched as filters
  // narrow it — otherwise choosing "Toyota" would remove every other store
  // from the dropdown that chose it.
  useEffect(() => {
    if (!expanded || filterOptions !== null || slug === DEALER_DUPLICATE_SLUG) return
    let cancelled = false
    getGroupFilterOptions(runId, slug, rooftopSlug)
      .then((options) => { if (!cancelled) setFilterOptions(options) })
      // A failed options fetch leaves the dropdowns disabled; the rows, which
      // are the point of the group, still load.
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [expanded, filterOptions, runId, slug, rooftopSlug])

  // Ascending first, then descending, then back to the group's own order — so
  // a mis-click is undone by clicking twice more rather than being stuck.
  const cycleSort = (field: string) => {
    setPage(1)
    setSort((current) => {
      if (current?.field !== field) return { field, direction: "asc" }
      if (current.direction === "asc") return { field, direction: "desc" }
      return null
    })
  }

  const severity = info?.severity ?? "cleanup"

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    // Wrap, or a narrow screen clips the tail of this row: the NEW/RESOLVED
    // badge is flex:none and the card sets overflow:hidden, so with nowrap the
    // badge ran under the card edge and lost its last characters on a phone.
    flexWrap: "wrap",
    gap: 12,
    padding: "12px 16px",
    cursor: "pointer",
    userSelect: "none",
    background: THEME.colors.white,
    border: "none",
    width: "100%",
    textAlign: "left",
    fontFamily: THEME.fonts.primary,
  }

  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.md,
        marginBottom: 10,
        overflow: "hidden",
        background: THEME.colors.white,
      }}
    >
      <button type="button" onClick={onToggle} style={headerStyle} aria-expanded={expanded}>
        <span
          style={{
            color: THEME.colors.neutral.light.textSecondary,
            fontSize: 11,
            width: 12,
            flex: "none",
            display: "inline-block",
            transform: expanded ? "rotate(90deg)" : undefined,
          }}
        >
          {/* U+25B8, not U+25B6: the latter is an emoji codepoint, and
              iOS has no non-emoji font covering it, so the caret rendered as a
              colour play-button on every group row. Same family as the sort
              arrows in AuditTable and MultiSelectDropdown. */}
          ▸
        </span>
        <SeverityDot severity={severity} />
        <span style={{ fontSize: 14, fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}>
          {info?.label ?? slug}
        </span>
        <span
          style={{
            fontSize: 13,
            color: THEME.colors.neutral.light.textSecondary,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count.toLocaleString()} {count === 1 ? "vehicle" : "vehicles"}
        </span>
        {newVins !== undefined && newVins.size > 0 && (
          <span
            style={{
              borderRadius: THEME.borderRadius.pill,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.6px",
              padding: "2px 8px",
              background: THEME.problemDisplay.severitySoft.info,
              color: THEME.problemDisplay.severityColors.info,
              flex: "none",
            }}
          >
            +{newVins.size} NEW
          </span>
        )}
        {/* A critical group is past its grace by definition, so it carries how
            long its oldest car has been open where a warning carries NEW
            (revenue-assurance plan). Same pill shape as NEW, in the quiet
            cleanup grey so it informs without competing with the red dot. */}
        {severity === "critical" && oldestFirstSeenAt && (
          <span
            style={{
              borderRadius: THEME.borderRadius.pill,
              fontSize: 10, fontWeight: 600, letterSpacing: "0.3px",
              padding: "2px 8px",
              background: THEME.problemDisplay.severitySoft.cleanup,
              color: THEME.problemDisplay.severityColors.cleanup,
              flex: "none",
            }}
          >
            oldest {formatDaysOpen(oldestFirstSeenAt)}
          </span>
        )}
        {info && !isMobile && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 12.5,
              color: THEME.colors.neutral.light.textSecondary,
              maxWidth: "46ch",
              textAlign: "right",
            }}
          >
            {info.action}
          </span>
        )}
      </button>
      {expanded && (
        <div style={{ borderTop: `1px solid ${THEME.colors.neutral.light.border}` }}>
          {/* The filter bar sits ABOVE the state split rather than inside the
              has-rows branch. Filtering a group down to nothing, or a fetch
              that failed, used to unmount it — and it carries the only Clear
              button, so the operator was left holding a filter they could not
              undo and a group that read as permanently empty. */}
          {slug !== DEALER_DUPLICATE_SLUG && (vehicles !== null || error !== null) && (
            <GroupFilterBar
              options={filterOptions}
              filters={filters}
              onChange={(next) => { setPage(1); setFilters(next) }}
              shown={total}
              total={count}
              // Under a store view every row is already one store's — a
              // rooftop dropdown there could only echo or empty the group.
              showRooftopFilter={rooftopSlug === null}
            />
          )}
          {error !== null ? (
            <div style={{ ...messageStyle, color: THEME.colors.semantic.danger }}>
              <span>{error}</span>
              <button
                type="button"
                onClick={() => { setError(null); setReloadNonce((n) => n + 1) }}
                style={retryButtonStyle}
              >
                Retry
              </button>
            </div>
          ) : vehicles === null ? (
            <div style={messageStyle}>Loading…</div>
          ) : vehicles.length === 0 ? (
            // Which sentence depends on whether the operator narrowed the
            // group themselves — "no vehicles in this group" on a group they
            // just filtered reads as a fault in the app rather than an answer.
            <div style={messageStyle}>
              {countActiveFilters(filters) > 0
                ? "No vehicles match these filters."
                : "No vehicles in this group."}
            </div>
          ) : (
            <>
              {requestKey !== loadedKey && (
                <div style={{ padding: "6px 16px", fontSize: 12, color: THEME.colors.neutral.light.textSecondary }}>
                  Loading…
                </div>
              )}
              <GroupVehicleTable
                vehicles={vehicles}
                severity={severity}
                newVins={newVins}
                workedMarks={workedMarks}
                onRowClick={onRowClick}
                sort={sort}
                // Dealer Duplicates is never sorted (Ian): its rows are shown
                // as VIN families, and on run 86 twelve of the fourteen
                // families spanned two stores with a different age, stock and
                // status on each row — so any column sort scatters the pairing
                // that IS the finding. Passing no handler leaves the headers
                // plain rather than offering a click that does nothing.
                onSort={slug === DEALER_DUPLICATE_SLUG ? undefined : cycleSort}
              />
              {total > GROUP_PAGE_SIZE && (
                <GroupPager
                  page={page}
                  total={total}
                  shown={vehicles.length}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Compact pager under a group's table — mirrors the sheet view's
 * PaginationFooter reading ("Showing X–Y of Z · Page N of M") at group scale.
 */
function GroupPager({
  page,
  total,
  shown,
  onPageChange,
}: {
  page: number
  total: number
  shown: number
  onPageChange: (next: number) => void
}) {
  const totalPages = Math.max(1, Math.ceil(total / GROUP_PAGE_SIZE))
  const startItem = (page - 1) * GROUP_PAGE_SIZE + 1
  const endItem = (page - 1) * GROUP_PAGE_SIZE + shown

  const buttonStyle = (disabled: boolean): React.CSSProperties => ({
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 500,
    fontFamily: THEME.fonts.primary,
    borderRadius: THEME.borderRadius.sm,
    border: `1px solid ${THEME.colors.silver}`,
    backgroundColor: THEME.colors.neutral.light.surface,
    color: disabled
      ? THEME.colors.neutral.light.textSecondary
      : THEME.colors.neutral.light.textPrimary,
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  })

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: THEME.spacing.sm,
        flexWrap: "wrap",
        padding: "8px 16px",
        borderTop: `1px solid ${THEME.colors.neutral.light.border}`,
        background: THEME.problemDisplay.legendBackground,
        fontSize: 12.5,
        color: THEME.colors.neutral.light.textSecondary,
      }}
    >
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        Showing {startItem.toLocaleString()}–{endItem.toLocaleString()} of {total.toLocaleString()} vehicles
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm }}>
        <button
          type="button"
          style={buttonStyle(page <= 1)}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          ‹ Prev
        </button>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          style={buttonStyle(page >= totalPages)}
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next ›
        </button>
      </span>
    </div>
  )
}
